export type Size = [number, number] | "fluid";

/**
 * Extracts first size from Google's GeneralSize object.
 * @see {@link https://developers.google.com/doubleclick-gpt/reference#googletag.GeneralSize}
 *
 * NOTE: Let's keep this function intact, so it's easier to follow, disable complexity rule
 *
 * @param generalSize of the ad slot
 * @returns the first size from the GeneralSize object
 */
// eslint-disable-next-line complexity
export function extractSingleSizeFromGeneralSize(
  generalSize: googletag.GeneralSize,
): Size {
  if (Array.isArray(generalSize)) {
    /**
     * First child is either:
     * - a number, then the entire array should be [width, height] - that's the size array we're looking for
     * - a string, then the entire array should be ['fluid'] - that's the fluid size we're looking for
     * - array, then the size is probably nested inside is, so let's recursively call this function
     * - something else - then we don't know what to do with it, so let's just return [1, 1]
     */
    // if the first element is a number, then it's a size array
    if (
      generalSize.length === 2 &&
      typeof generalSize[0] === "number" &&
      typeof generalSize[1] === "number"
    ) {
      return generalSize as Size;
    }
    // if the first element is a string, then it's a fluid size
    if (generalSize.length === 1 && generalSize[0] === "fluid") {
      return "fluid";
    }
    // if the first element is an array, then it's a nested size array
    if (Array.isArray(generalSize[0])) {
      return extractSingleSizeFromGeneralSize(generalSize[0]);
    }
    // if the first element is something else, then we don't know what to do with it
    // so let's just return [1, 1]
    return [1, 1];
  }
  // if the param is not an array, then it's a string
  if (generalSize === "fluid") {
    return "fluid";
  }
  // we do not know what to do with it, so let's just return [1, 1]
  return [1, 1];
}
